import React, { useCallback, useContext, useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { InstantSearch } from 'react-instantsearch-hooks-web';
import algoliasearch from 'algoliasearch/lite';
import { CSSTransition } from 'react-transition-group';
import cookie from 'js-cookie';
import Header from '../components/Header';
import { DEFAULT_CATEGORY_MENU } from '../constants/default';
import { GRASSDOOR } from '../constants/website';
import Page404 from './404';
import getHost from '../helpers/gethost';
import pageDisable from '../helpers/pageDisableHelper';
import {
  useFullCategoryList,
  useASAPBestSellerShop,
  useScheduleBestSellerShop,
  useRecentViewedProducts,
  useTopBrandsList
} from '../hooks/Shop';
import getDefaultTab from '../helpers/getDefaultTab';
import * as COOKIE_VARIABLES from '../constants/cookieVariables';
import { useDeliveryDetails } from '../hooks/app';
import { ASAP, SCHEDULE } from '../constants/deliveryType';
import { isNewHomePageApplicable, isSearchPageApplicable } from '../constants/feature';
import CategoryMenu from '../components/DTC/CategoryMenu';
import SearchBox from '../components/searchModule/SearchInput';
import FixedBottomBar from '../components/FixedBottomBar';
import { useScrollTop } from '../hooks/scroll';
import SearchStats from '../components/searchModule/SearchStats';
import { RESULT_FOUND, RESULT_NOT_FOUND, RESULT_TYPO_TOLERENCE } from '../constants/search';
import storageService from '../services/storageService';
import RecentSearch from '../components/searchModule/RecentSearch';
import IndividualCategory from '../components/DTC/grassdoor/IndividualCategory';
import SearchCategories from '../components/searchModule/SearchCategories';
import SearchBrands from '../components/searchModule/SearchBrands';
import { MobileFilter } from '../components/searchModule/MobileFilter';
import DesktopFilter from '../components/searchModule/DesktopFilter';
import SelectedFilterList from '../components/searchModule/SelectedFilterList';
import FullscreenLoader from '../components/FullScreenLoader';
import useAPI from '../NetworkCall/API';
import { getBringItAgainOrders } from '../hooks/User';
import InlineLoader from '../components/InlineLoader';
import { useDispatch, useSelector } from 'react-redux';
import { setProductFetchInfo } from '../redux/slices/userSlice';
import { setRecentSearch, setSearchState } from '../redux/slices/searchSlice';
import { withConfig } from '../lib/withConfig';
import { useConfigData } from '../Context/ConfigProvider';

const canonicalHref = `https://${getHost()}/search`;

const Search = ({ error }) => {
  const search = useSelector(state => state.search)
  const {
    searchQuery,
    searchProductCount: { asapCount, scheduleCount },
    searchResutState,
    recentSearches
  } = search
  const dispatchRedux = useDispatch()

  if (error) {
    return <Page404 />;
  }

  const {
    data: { asapEnabled = true, scheduleEnabled = true, lastSlotCutOff, asapEndTime, asapStartTime } = {},
    isLoading: isAddressLoading
  } = useDeliveryDetails();

  const { data: fullCategoryList = DEFAULT_CATEGORY_MENU, isLoading: isCategoriesLoading } = useFullCategoryList({
    revalidate: true
  });
  const { isLoading: isBrandLoading } = useTopBrandsList({ initialTopBrandsList: null });

  const { data: recentViewedProducts, isLoading: recentViewedLoading } = useRecentViewedProducts({});
  const recentViewedProductsId = cookie.get(COOKIE_VARIABLES.recentViewedProductsId)
    ? JSON.parse(cookie.get(COOKIE_VARIABLES.recentViewedProductsId))
    : null;

  const [listLoading, setListLoading] = useState(false);
  const [similarProductsLoading, setSimlarProductsLoading] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const [topSearchLoading, setTopSearchLoading] = useState(true);
  const [topSearches, setTopSearch] = useState([]);
  const { getTopSearches } = useAPI();

  useEffect(async () => {
    try {
      setTopSearchLoading(true);
      let response = await getTopSearches();
      response = response?.filter(item => item?.search?.trim().length > 2 && item?.search?.trim().length < 11).slice(0, 10);
      setTopSearch(response);
      setTopSearchLoading(false);
    } catch (error) {
      setTopSearchLoading(false);
    }
  }, []);

  const router = useRouter();
  const {
    query: { tab }
  } = router;

  const { tab: pathName } = router.query;

  const { data: asapCategories } = useASAPBestSellerShop({});

  useEffect(() => {
    if (searchResutState === RESULT_NOT_FOUND) {
      dispatchRedux(setProductFetchInfo({ shouldFetchProduct: true }))
    }
    return () => {
      dispatchRedux(setProductFetchInfo({ shouldFetchProduct: false }))
    };
  }, [searchResutState]);

  useEffect(() => {
    return () => {
      dispatchRedux(setSearchState(''))
    };
  }, []);

  const { data: scheduleCategories } = useScheduleBestSellerShop({});

  const { data: bringItAsapProducts } = getBringItAgainOrders({ deliveryType: 1 });
  const { data: bringItScheduleProducts } = getBringItAgainOrders({ deliveryType: 2 });

  let currentTab = tab || getDefaultTab({ lastSlotCutOff, asapStartTime, asapEndTime, asapEnabled });

  if (!asapEnabled) {
    currentTab = SCHEDULE;
  }

  if (!scheduleEnabled) {
    currentTab = ASAP;
  }

  let bothTypeSchedule = false;
  if (scheduleEnabled && asapEnabled) {
    bothTypeSchedule = true;
  }

  const currentSubCategory = tab == ASAP ? asapCategories : scheduleCategories;

  useEffect(() => {
    if (currentTab == ASAP && pathName == SCHEDULE && !bothTypeSchedule && !isAddressLoading) {
      router.push(`/search?tab=${ASAP}`);
    } else if (currentTab == SCHEDULE && pathName == ASAP && !bothTypeSchedule && !isAddressLoading) {
      router.push(`/search?tab=${SCHEDULE}`);
    }
  }, [pathName, currentTab, bothTypeSchedule, isAddressLoading]);

  const { scrollDirectionTop } = useScrollTop();
  const [displayFreightBanner, setDisplayFreightBanner] = useState(true);
  const [isFilterPopupVisible, toggleFilterPopup] = useState(false);
  const appConfig = useConfigData();

  const [isSolid, setSolid] = useState(false);

  const handleScroll = useCallback(() => {
    if (window.scrollY > 150) {
      if (!isSolid) {
        setSolid(true);
      }
    } else if (isSolid) {
      setSolid(false);
    }
  }, [isSolid]);

  const storeRecentSearch = useCallback(() => {
    setNewRecentSearchValue(searchQuery);
  }, [searchQuery, recentSearches, searchResutState]);

  const setNewRecentSearchValue = query => {
    if (
      searchQuery?.trim().length > 3 &&
      (searchResutState == RESULT_FOUND || searchResutState == RESULT_TYPO_TOLERENCE) &&
      recentSearches?.[0] !== searchQuery
    ) {
      const newRecentSearch = recentSearches
        .filter(item => item?.toLowerCase()?.trim() != query?.toLowerCase()?.trim())
        .slice(0, 9);
      dispatchRedux(setRecentSearch([query, ...newRecentSearch]))
      storageService.setRecentSearch([query, ...newRecentSearch]);
    }
  };

  const removeRecentSearch = item => {
    const newRecentSearch = recentSearches.filter(data => data != item);
    dispatchRedux(setRecentSearch(newRecentSearch))
    storageService.setRecentSearch(newRecentSearch);
  };

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    document.addEventListener('scroll', storeRecentSearch);

    return () => {
      document.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', storeRecentSearch);
    };
  }, [handleScroll, storeRecentSearch]);

  function setTab(newTab) {
    if (tab !== newTab) {
      const href = `${router.pathname}?tab=${newTab}`;
      router.replace(href, undefined, { shallow: true });
    }
  }

  const trimmedSearchQuery = searchQuery?.trim();
  const appName = appConfig?.APP_NAME || '';
  const showBrands =
    !listLoading && (!trimmedSearchQuery || searchResutState === RESULT_NOT_FOUND || searchResutState === RESULT_TYPO_TOLERENCE);
  const showCategories =
    !listLoading && (!trimmedSearchQuery || searchResutState === RESULT_NOT_FOUND || searchResutState === RESULT_TYPO_TOLERENCE);
  const showTopSearches =
    !listLoading && (!trimmedSearchQuery || searchResutState === RESULT_NOT_FOUND || searchResutState === RESULT_TYPO_TOLERENCE);
  const showTopSellerProducts = trimmedSearchQuery && searchResutState === RESULT_NOT_FOUND;
  const showFilters = trimmedSearchQuery && (searchResutState === RESULT_FOUND || searchResutState === RESULT_TYPO_TOLERENCE);
  const showProductCategories =
    trimmedSearchQuery && (searchResutState === RESULT_FOUND || searchResutState === RESULT_TYPO_TOLERENCE);
  const isLoading =
    !showProductCategories &&
    isCategoriesLoading &&
    isBrandLoading &&
    recentViewedLoading &&
    recentViewedProductsId &&
    topSearchLoading;
  const showFixedBottom = asapEnabled && scheduleEnabled;

  useEffect(() => {
    if (!trimmedSearchQuery) setListLoading(false)
  }, [trimmedSearchQuery])

  return (
    <>
      <Head>
        <title>{appName}</title>
        <meta name="description" content={appName} />

        {(process.env.NEXT_PUBLIC_APP === GRASSDOOR || process.env.NEXT_PUBLIC_DEFAULT_SITES) && (
          <meta property="og:image" content={appConfig.META_IMAGE} />
        )}

        <link rel="canonical" href={canonicalHref} />
      </Head>
      {similarProductsLoading ? <FullscreenLoader hasTransparentBg /> : null}
      <Header
        hasWhiteBg={isSolid}
        defaultSearchTab="schedule"
        backPath
        toPath="/"
        setTab={setTab}
        currentTab={currentTab}
        isSearchPage
        search={false}
        showLogin={false}
        asapProductsCount={listLoading ? '...' : asapCount}
        scheduledProductsCount={listLoading ? '...' : scheduleCount}
        showUserInfo={isNewHomePageApplicable}
        showDeliverySpecificMessage={isNewHomePageApplicable}
        showProfile
        help
        hasShadow={false}
      />
      <div className="shop-full-menu search-page ">
        <div id="search-category-wrapper" className={`search-category-wrapper `}>
          <div className="searchBox">
            <SearchBox
              currentTab={currentTab}
              storeRecentSearch={storeRecentSearch}
              toggleFilterPopup={toggleFilterPopup}
              filterApplied={filterApplied}
            />
          </div>
          {showFilters ? (
            <div className="container mb-0 mb-lg-3">
              <div className="filter-product-page">
                <div className="g-right-sec">
                  <div className="">
                    <div className="mt-lg-3">
                      <DesktopFilter
                        currentTab={currentTab}
                        deviceType="desktop"
                        shadowHeight={750}
                        setListLoading={setListLoading}
                        listLoading={listLoading}
                      >
                        <SelectedFilterList
                          setListLoading={setListLoading}
                          filtersType="shopFilter"
                          className="sticky-filter-list"
                          setFilterApplied={setFilterApplied}
                        />
                      </DesktopFilter>
                      <SelectedFilterList
                        filtersType="searchFilter"
                        className="d-lg-none"
                        setListLoading={setListLoading}
                        setFilterApplied={setFilterApplied}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <div className="search-results-count">
          {trimmedSearchQuery ? (
            <div className="d-block container">
              <SearchStats currentTab={currentTab} setTab={setTab} setListLoading={setListLoading} listLoading={listLoading} />
            </div>
          ) : null}
        </div>
        <div className="container pt-2">
          {isLoading ? (
            <div className="text-center my-3">
              <InlineLoader positionRelative className="big" />
            </div>
          ) : (
            <>
              {showProductCategories && !listLoading ? (
                <SearchCategories
                  isSearchedProducts
                  isSearchPage
                  hideSlider
                  currentTab={currentTab}
                  bringItProducts={currentTab == SCHEDULE ? bringItScheduleProducts : bringItAsapProducts}
                  setSimlarProductsLoading={setSimlarProductsLoading}
                  fullCategoryList={fullCategoryList}
                />
              ) : null}

              {showProductCategories && !listLoading ? <SearchBrands currentTab={currentTab} /> : null}

              {!trimmedSearchQuery && recentSearches?.length ? (
                <RecentSearch
                  recentSearches={recentSearches}
                  removeRecentSearch={removeRecentSearch}
                  setNewRecentSearchValue={setNewRecentSearchValue}
                />
              ) : null}
              {!trimmedSearchQuery && recentViewedProducts?.length ? (
                <>
                  <div className="mb-3 mb-lg-5">
                    <div className="recent-search-text">Recently Viewed by You</div>
                    <div className="search-product recent-products">
                      <IndividualCategory data={{ products: recentViewedProducts }} isSearchPage />
                    </div>
                  </div>
                </>
              ) : null}
              {showTopSearches && topSearches?.length ? (
                <div className="mb-5">
                  <h5 className="search-text mt-1 top-search">
                    Top Searches <span className="icon-top-search ml-2" />
                  </h5>
                  <div className="d-flex mobile-scroll-pills">
                    {topSearches.map(item => (
                      <div key={item?.search} className="search-pill cursor-pointer">
                        <span
                          onClick={() => {
                            // dispatch({ type: 'setSearchState', payload: item?.search });
                            dispatchRedux(setSearchState(item?.search))
                            window.scrollTo(0, 0);
                          }}
                          className="px-3"
                        >
                          {item?.search}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {showTopSellerProducts && currentSubCategory?.length
                ? currentSubCategory.map(cat => (
                  <div className="mb-5" key={cat?.id}>
                    <div className="search-tab-title mb-1">{cat?.category_name}</div>
                    <div className="search-product recent-products">
                      <IndividualCategory data={cat} isSearchPage />
                    </div>
                  </div>
                ))
                : null}
              {/* {showBrands && !appConfig.hideBrandPage ? <BrandMenu title="Shop from Top Brands" isSearchPage /> : null} */}
              {showCategories && fullCategoryList?.length ? (
                <div className="my-5">
                  <h2 className="recent-search-text mb-4">Shop from Categories</h2>
                  <CategoryMenu fullCategoryList={fullCategoryList} />
                </div>
              ) : null}
            </>
          )}
        </div>
        {showFixedBottom && false && (
          <FixedBottomBar
            scrollDirectionTop={scrollDirectionTop}
            tab={currentTab}
            setTab={setTab}
            displayFreightBanner={displayFreightBanner}
            asapEnabled={asapEnabled}
            scheduleEnabled={scheduleEnabled}
            asapProductsCount={listLoading ? '...' : asapCount}
            scheduledProductsCount={listLoading ? '...' : scheduleCount}
            hideFilters
            toggleFilterPopup={toggleFilterPopup}
            setDisplayFreightBanner={setDisplayFreightBanner}
            isSearchPage
          />
        )}

        <div className={`${isFilterPopupVisible ? 'd-block d-lg-none' : 'd-none'}`}>
          <CSSTransition appear in mountOnEnter timeout={300} classNames="slideup-wrapper" unmountOnExit>
            <MobileFilter
              toggleFilterPopup={toggleFilterPopup}
              asapProductsCount={asapCount}
              scheduledProductsCount={scheduleCount}
              currentTab={currentTab}
              isFilterPopupVisible={isFilterPopupVisible}
            />
          </CSSTransition>
        </div>
      </div>
    </>
  );
};

const SearchWrapper = React.memo(({ error }) => {
  const appConfig = useConfigData();
  const searchClient = algoliasearch(appConfig.SEARCH_CONTAINER || '', appConfig.SEARCH_KEY || '');

  return (
    <InstantSearch indexName={appConfig.SEARCH_INDICE} searchClient={searchClient}>
      <Search />
    </InstantSearch>
  )
})

export async function getServerSidePropsFunction({ req, res }) {
  if (!isSearchPageApplicable) {
    return { props: { error: 404 } };
  }
  return {
    props: {}
  };
}
export const getServerSideProps = withConfig(getServerSidePropsFunction)
export default pageDisable(SearchWrapper);
